/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

// Component imports
import { Button, Card, Col, Empty, Form, InputNumber, Popover, Radio, Row, Space, Spin, Tag, Typography, Table } from 'antd';
import { ArrowDownOutlined, MessageOutlined } from '@ant-design/icons';
import { DecisionIcon } from '../Icons/DecisionIcon';
import ReactMarkdown from 'react-markdown';
import RichTextEditor from './Fields/RichTextEditor/RichTextEditor';
import EntryPointSelect from './Fields/EntryPointSelect';
import LabelWithOriginalText from './Fields/LabelWithOriginalText';
import ReactMarkdownList from '../UI/ReactMarkdownList';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { ACTIVITY_TYPES, PERMISSIONS_GROUP_EDITOR, VARIABLE_TYPES } from '../../constants/main';
import { LIVECHAT_EVENT_TYPES } from '../../constants/liveChat';
import { NODE_TYPES, NODE_TYPES_PROPERTIES } from '../../constants/flowGraph';
import { automatedAnswerToChaptersource } from '../../utils/transform/automatedAnswer';
import { IS_USER_FEEDBACK_ENABLED } from '../../constants/env';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { useFetchLiveChatSystemMessagesQuery, useUpdateLiveChatSystemMessagesMutation } from '../../store/liveChatSystemMessages/liveChatSystemMessagesApi';
import { useLazyFetchStoryVariablesQuery } from '../../store/story/storyApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import useTheme from '../../utils/hooks/useTheme';
import { INTEGRATION_TYPE } from '../../constants/integrations';

// Constant declarations
const MESSAGE_TYPE = {
  [LIVECHAT_EVENT_TYPES.LIVECHAT_GENERIC_ERROR]: 'These messages will be sent when an error occurs',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_IS_DISABLED]: 'These messages will be sent when LiveChat is disabled',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_IS_IN_PROGRESS]: 'These messages will be sent when a conversation is already in progress',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_OUT_OF_BUSINESS_HOURS]: 'These messages will be sent when a request is made out of business hours',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_ACCEPTED]: 'These messages will be sent when an LiveChat Agent accepts the request',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_EXISTS]: 'These messages will be sent when a request is already pending',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_MISSED]: 'These messages will be sent when the user\'s request expires',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_TERMINATED]: 'These messages will be sent when a conversation is terminated',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_TRANSFERRED]: 'These messages will be sent when a conversation is transferred to another LiveChat Agent',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_CLOSED]: 'These messages will be sent when a LiveChat request is cancelled'
};

const LIVECHAT_NODE_FRIENDLY_NAME = {
  [LIVECHAT_EVENT_TYPES.LIVECHAT_GENERIC_ERROR]: i18n.t('Generic error'),
  [LIVECHAT_EVENT_TYPES.LIVECHAT_IS_DISABLED]: i18n.t('LiveChat disabled'),
  [LIVECHAT_EVENT_TYPES.LIVECHAT_IS_IN_PROGRESS]: i18n.t('Conversation in progress'),
  [LIVECHAT_EVENT_TYPES.LIVECHAT_OUT_OF_BUSINESS_HOURS]: i18n.t('Out of business hours'),
  [LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_ACCEPTED]: i18n.t('Request accepted'),
  [LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_EXISTS]: i18n.t('Request already exists'),
  [LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_MISSED]: i18n.t('Request missed'),
  [LIVECHAT_EVENT_TYPES.LIVECHAT_TERMINATED]: i18n.t('Conversation terminated'),
  [LIVECHAT_EVENT_TYPES.LIVECHAT_TRANSFERRED]: i18n.t('Conversation transferred'),
  [LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_CLOSED]: i18n.t('Request Cancelled')
};

const REDIRECT_ENABLED = {
  [LIVECHAT_EVENT_TYPES.LIVECHAT_IS_DISABLED]: 'LiveChat disabled',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_TERMINATED]: 'Conversation terminated',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_MISSED]: 'Request missed',
  [LIVECHAT_EVENT_TYPES.LIVECHAT_OUT_OF_BUSINESS_HOURS]: 'Out of business hours'
};

const SystemMessageFormItems = ({ form, systemMessageActivity, tenantId }) => {
  const { orgId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const { t } = useTranslation();
  const { botLanguages, currentBotLanguage, primaryBotLanguage, isEditingPrimaryLanguage } = useBotLanguage(tenantId);
  const { messageNodeIconColor, jumpNodeIconColor } = useTheme();

  const formPrefix = systemMessageActivity.id;

  const isViewOnly = useCallback(() => {
    return !PERMISSIONS_GROUP_EDITOR.some(permission => userPermissions.includes(permission));
  }, [userPermissions]);

  const isUserFeedbackAllowed = useCallback(() => {
    return IS_USER_FEEDBACK_ENABLED && systemMessageActivity && systemMessageActivity.content.nodes.find(node => node.type === NODE_TYPES.INTRO)?.id === LIVECHAT_EVENT_TYPES.LIVECHAT_TERMINATED;
  }, [systemMessageActivity]);

  const isRedirectAllowed = useCallback(() => {
    return systemMessageActivity && Object.keys(REDIRECT_ENABLED).includes(systemMessageActivity.content.nodes.find(node => node.type === NODE_TYPES.INTRO)?.id);
  }, [systemMessageActivity]);

  const isRadioGroupEnabled = useCallback((isLastIndex) => {
    const isUserFeedbackEnabled = isUserFeedbackAllowed();
    const isRedirectEnabled = isRedirectAllowed();

    if (isRedirectEnabled && !isUserFeedbackEnabled) {
      if (isLastIndex) {
        return true;
      } else {
        return false;
      }
    } else {
      return isUserFeedbackEnabled || isRedirectEnabled;
    }
  }, [systemMessageActivity]);

  return (
    <Form.List name={[formPrefix, 'responses']}>
      {
        (responsesFields, { add: addResponse, remove: removeResponse }, { errors: responsesErrors }) => (
          <Fragment>
            <Space
              direction="vertical"
              size={0}
              className="w-100"
              split={
                <Row className="w-100 mb-3" justify="center">
                  <Col>
                    {/* Arrow Down to be displayed between two messages/feedback/redirect nodes */}
                    <ArrowDownOutlined className="text-secondary" />
                    {/* Arrow Down to be displayed between two messages/feedback/redirect nodes */}
                  </Col>
                </Row>
              }
            >
              {
                responsesFields.map(({ key: responseKey, name: responseIndex }) => (
                  <Row align="middle">
                    <Col xs={22}>
                      <Card key={responseKey} className="mb-5 p-2" size="small">
                        <Row>
                          <Col flex="auto">
                            {
                              isViewOnly() || !isRadioGroupEnabled(responseIndex === responsesFields.length - 1) ? (
                                <h3 className="mb-0">
                                  {
                                    form.getFieldValue([formPrefix, 'responses', responseIndex, 'type']) === NODE_TYPES.JUMP ? (
                                      <DecisionIcon style={{ color: jumpNodeIconColor }} className="mr-2" />
                                    ) : (
                                      <MessageOutlined style={{ color: messageNodeIconColor }} className="mr-2" />
                                    )
                                  }

                                  {t(form.getFieldValue([formPrefix, 'responses', responseIndex, 'type']), { context: 'automated answer response type' })}
                                </h3>
                              ) : (
                                <Form.Item
                                  name={[responseIndex, 'type']}
                                  className="mb-0"
                                >
                                  <Radio.Group>
                                    <Radio key={NODE_TYPES.MESSAGE} value={NODE_TYPES.MESSAGE} disabled={!isEditingPrimaryLanguage}>
                                      <Space>
                                        {NODE_TYPES_PROPERTIES[NODE_TYPES.MESSAGE]?.icon}
                                        {t(NODE_TYPES.MESSAGE, { context: 'automated answer response type' })}
                                      </Space>
                                    </Radio>

                                    {
                                      isUserFeedbackAllowed() ? (
                                        <Radio key={NODE_TYPES.USER_FEEDBACK} value={NODE_TYPES.USER_FEEDBACK} disabled={!isEditingPrimaryLanguage}>
                                          <Space>
                                            {NODE_TYPES_PROPERTIES[NODE_TYPES.USER_FEEDBACK]?.icon}
                                            {t(NODE_TYPES.USER_FEEDBACK, { context: 'automated answer response type' })}
                                          </Space>
                                        </Radio>
                                      ) : null
                                    }

                                    {
                                      // Only the last node can be of type "Jump"
                                      isRedirectAllowed() && responseIndex === responsesFields.length - 1 ? (
                                        <Radio key={NODE_TYPES.JUMP} value={NODE_TYPES.JUMP} disabled={!isEditingPrimaryLanguage}>
                                          <Space>
                                            {NODE_TYPES_PROPERTIES[NODE_TYPES.JUMP]?.icon}
                                            {t(NODE_TYPES.JUMP, { context: 'automated answer response type' })}
                                          </Space>
                                        </Radio>
                                      ) : null
                                    }
                                  </Radio.Group>
                                </Form.Item>
                              )
                            }
                          </Col>
                        </Row>

                        {
                          form.getFieldValue([formPrefix, 'responses', responseIndex, 'type']) === NODE_TYPES.MESSAGE ? (
                            <Fragment>
                              <Space className="mb-3 mt-1" wrap={false} align="start">
                                <Typography.Text className="text-secondary">{t('Agent will display the below message')}</Typography.Text>
                              </Space>

                              <Row gutter={16} wrap={false}>
                                <Col flex="auto">
                                  {
                                    isViewOnly() ? (
                                      <Card bordered className="p-0">
                                        <div className="px-3">
                                          {
                                            form.getFieldValue([formPrefix, 'responses', responseIndex, 'options', 0, currentBotLanguage]) ? (
                                              <Typography.Text ellipsis>
                                                <ReactMarkdown
                                                  className="mt-3 w-100"
                                                  skipHtml
                                                  linkTarget="_blank"
                                                >
                                                  {form.getFieldValue([formPrefix, 'responses', responseIndex, 'options', 0, currentBotLanguage])}
                                                </ReactMarkdown>
                                              </Typography.Text>
                                            ) : (
                                              <Typography.Text type="secondary">
                                                {t('(no response)')}
                                              </Typography.Text>
                                            )
                                          }
                                        </div>
                                      </Card>
                                    ) : (
                                      <Fragment>
                                        <LabelWithOriginalText originalText={form.getFieldValue([formPrefix, 'responses', responseIndex, 'options', 0, primaryBotLanguage])} tenantId={tenantId} />
                                        {
                                          botLanguages.map(lang => (
                                            <Form.Item
                                              name={[responseIndex, 'options', 0, lang]}
                                              validateTrigger={['onChange', 'onBlur']}
                                              hidden={currentBotLanguage !== lang}
                                              rules={[{ required: lang === primaryBotLanguage, message: t('Please enter a response option.') }]}
                                              noStyle
                                            >
                                              <RichTextEditor
                                                placeholder={t('Response text')}
                                                index={responseIndex}
                                                disabled={isViewOnly()}
                                                spellCheckLanguage={false}
                                                includedVariableTypes={[VARIABLE_TYPES.LIVECHAT]}
                                                botId={tenantId}
                                              />
                                            </Form.Item>
                                          ))
                                        }
                                      </Fragment>
                                    )
                                  }
                                </Col>
                              </Row>
                            </Fragment>
                          ) : null
                        }

                        {
                          // Conditional fields for "user feedback" type responses
                          form.getFieldValue([formPrefix, 'responses', responseIndex, 'type']) === NODE_TYPES.USER_FEEDBACK ? (
                            <Fragment>
                              <Space className="mb-3 mt-1" wrap={false} align="start">
                                <Typography.Text className="text-secondary">{t('Agent will redirect to the selected Feedback Flow')}</Typography.Text>
                              </Space>

                              <Form.Item
                                validateTrigger={['onChange', 'onBlur']}
                                rules={[
                                  {
                                    required: true,
                                    message: t('Redirect target cannot be blank.')
                                  }
                                ]}
                                name={[responseIndex, 'jumpTo']}
                              >
                                <EntryPointSelect
                                  size="large"
                                  activityTypesToDisplay={[ACTIVITY_TYPES.USER_FEEDBACK]}
                                  disabled={!isEditingPrimaryLanguage}
                                />
                              </Form.Item>

                              <Form.Item
                                label={t('Probability to ask for feedback')}
                                name={[responseIndex, 'jumpProbabilityLocal']}
                                rules={[{ required: true, message: t('Please enter a probability.') }]}
                                initialValue={(Number(systemMessageActivity.jumpProbability) || 1) * 100}
                              >
                                <InputNumber
                                  size="large"
                                  min={0}
                                  max={100}
                                  addonAfter="%"
                                  onChange={(value) => form.setFieldValue([formPrefix, 'responses', responseIndex, 'jumpProbability'], value / 100)}
                                  disabled={!isEditingPrimaryLanguage}
                                />
                              </Form.Item>

                              <Form.Item
                                name={[responseIndex, 'jumpProbability']}
                                hidden
                              >
                                <InputNumber />
                              </Form.Item>
                            </Fragment>
                          ) : null
                        }

                        {
                          form.getFieldValue([formPrefix, 'responses', responseIndex, 'type']) === NODE_TYPES.JUMP ? (
                            <Fragment>
                              <Space className="mb-3 mt-1" wrap={false} align="start">
                                <Typography.Text className="text-secondary">{t('Agent will redirect to the selected Flow or Automated answer')}</Typography.Text>
                              </Space>

                              <Form.Item
                                validateTrigger={['onChange', 'onBlur']}
                                rules={[
                                  {
                                    required: true,
                                    message: t('Redirect target cannot be blank.')
                                  }
                                ]}
                                name={[responseIndex, 'jumpTo']}
                              >
                                <EntryPointSelect
                                  size="large"
                                  // Set Table as parent container to fix layering issues with dropdown
                                  getPopupContainer={(triggerNode) => {
                                    if (triggerNode?.closest && triggerNode.closest('.ant-table-wrapper')) {
                                      return triggerNode.closest('.ant-table-wrapper');
                                    }
                                    return document.body;
                                  }}/>
                              </Form.Item>
                            </Fragment>
                          ) : null
                        }
                      </Card>
                    </Col>

                    {
                      !isViewOnly() && responsesFields.length > 1 ? (
                        <Col xs={2} className="text-center">
                          <Button
                            type="link"
                            icon={<DeleteIcon />}
                            onClick={() => removeResponse(responseIndex)}
                            disabled={!isEditingPrimaryLanguage}
                          />
                        </Col>
                      ) : null
                    }
                  </Row>
                ))
              }
            </Space>

            {
              // Only show the Add button if the last response isn't of "Jump" type
              !isViewOnly() && form && form.getFieldValue([formPrefix, 'responses'])?.at(-1)?.type !== NODE_TYPES.JUMP ? (
                <Row gutter={16}>
                  <Col xs={22}>
                    <Button
                      size="large"
                      type="dashed"
                      icon={<PlusIcon/>}
                      block
                      className="text-muted bg-layout-gray"
                      disabled={!isEditingPrimaryLanguage}
                      onClick={() => addResponse({ type: NODE_TYPES.MESSAGE, options: [''] })}
                    >
                      {t('Add Agent Response')}
                    </Button>
                  </Col>
                </Row>
              ) : null
            }

            <Form.ErrorList errors={responsesErrors} />
          </Fragment>
        )}
    </Form.List>
  );
};

const LivechatSystemMessagesEditor = ({ form, tenantId, onFieldChange, onLoadingChange }) => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const { currentBotLanguage, primaryBotLanguage } = useBotLanguage();

  const [fetchStoryVariables] = useLazyFetchStoryVariablesQuery();
  const { data: fetchedBot, isFetching: isBotFetching } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: livechatSystemMessages, isFetching: isFetchingLivechatSystemMessages, refetch: fetchLivechatSystemMessages } = useFetchLiveChatSystemMessagesQuery({ orgId, tenantId, currentBotLanguage }, { skip: !tenantId });
  const helviaLivechatPlugin = useMemo(() => (fetchedBot?.plugins || []).find(plugin => plugin.integration.type === INTEGRATION_TYPE.HELVIA_LIVECHAT), [fetchedBot]);
  const genesysLivechatPlugin = useMemo(() => (fetchedBot?.plugins || []).find(plugin => plugin.integration.type === INTEGRATION_TYPE.GENESYS_LIVECHAT), [fetchedBot]);
  const [updateLivechatSystemMessages, { isLoading: isUpdatingLivechatSystemMessages }] = useUpdateLiveChatSystemMessagesMutation();

  const [expandedRows, setExpandedRows] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [, updateState] = useState(); // Hook to force a component re-render

  useEffect(() => {
    if (orgId && tenantId) {
      fetchStoryVariables({ orgId, tenantId });
    }
  }, [orgId, tenantId]);

  useEffect(() => {
    if (orgId && tenantId) {
      fetchLivechatSystemMessages({ orgId, tenantId });
    }
  }, [fetchedBot?.plugins]);

  useEffect(() => {
    if (isBotFetching) {
      setExpandedRows([]);
    }
  }, [isBotFetching]);

  const formatLivechatConversationNode = (name) => {
    return (name || '').replaceAll('-', '_');
  };

  useEffect(() => {
    if (livechatSystemMessages?.items?.length) {
      const itemToFilterOut = genesysLivechatPlugin ? 'LIVECHAT-TRANSFERRED' : 'LIVECHAT-OUT-OF-BUSINESS-HOURS';
      const data = ([...(helviaLivechatPlugin ? livechatSystemMessages.items : livechatSystemMessages.items.filter(item => item.id !== itemToFilterOut))]
        .sort((a, b) =>
          LIVECHAT_NODE_FRIENDLY_NAME[formatLivechatConversationNode(a.name[currentBotLanguage])]
            ?.localeCompare(LIVECHAT_NODE_FRIENDLY_NAME[formatLivechatConversationNode(b.name[currentBotLanguage])])
        ));
      setFilteredData(data);
    }
  }, [helviaLivechatPlugin, livechatSystemMessages, currentBotLanguage]);

  //Reset the fields when the language changes or data are updated
  useEffect(() => {
    form.resetFields();
  }, [tenantId, currentBotLanguage, filteredData, form]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isUpdatingLivechatSystemMessages);
    }
  }, [isUpdatingLivechatSystemMessages]);

  // Data structure to handle all message activities in one form (controlled by parent), use activity id as formPrefix
  const initialData = filteredData.reduce((acc, systemMessageActivity) => {
    acc[systemMessageActivity.id] = {
      responses: systemMessageActivity.content.nodes
        .filter(node => node.type !== NODE_TYPES.INTRO)
        .map(node => ({
          type: node.type,
          ...(node.type === NODE_TYPES.MESSAGE && { options: [node.label] }),
          ...(node.type === NODE_TYPES.JUMP && { options: [''], jumpTo: node.jumpTo }),
          ...(node.type === NODE_TYPES.USER_FEEDBACK && { options: [''], jumpTo: node.jumpTo, jumpProbability: node.jumpProbability })
        }))
    };
    return acc;
  }, {});

  const onSubmit = async (values) => {
    let isSubmitSuccessful = true;

    const allTransformedValues = [];

    // Iterate over each form list
    for (const systemMessageActivityId in values) {
      const { responses } = values[systemMessageActivityId];

      const systemMessageActivity = filteredData.find(activity => activity.id === systemMessageActivityId);

      if (systemMessageActivity) {
        const introNodeId = systemMessageActivity.content.nodes.find(node => node.type === NODE_TYPES.INTRO)?.id;

        // Construct the submit values for each activity
        const valuesToSubmit = {
          responses: [...responses],
          id: systemMessageActivity.id,
          name: systemMessageActivity.name
        };

        const transformedValues = automatedAnswerToChaptersource(
          valuesToSubmit,
          primaryBotLanguage,
          introNodeId,
          ACTIVITY_TYPES.LIVECHAT_SYSTEM_MESSAGE
        );

        allTransformedValues.push(transformedValues);
      }
    }

    isSubmitSuccessful = await updateLivechatSystemMessages({ orgId, tenantId, data: allTransformedValues });

    return isSubmitSuccessful;
  };

  const renderExtraResponseTags = (response) => {
    if (!response) {
      return null;
    }

    const tags = [];
    const nestedList = [];

    const userFeedbackNodes = response.content.nodes.filter(node => node.type === NODE_TYPES.USER_FEEDBACK);
    if (userFeedbackNodes.length > 0) {
      userFeedbackNodes.forEach(feedbackNode => (
        nestedList.push(
          <ReactMarkdownList
            message={feedbackNode.jumpTo}
            icon={NODE_TYPES_PROPERTIES[NODE_TYPES.USER_FEEDBACK]?.icon}
          />
        )
      ));
    }

    const jumpNode = response.content.nodes.find(node => node.type === NODE_TYPES.JUMP);
    if (jumpNode) {
      nestedList.push(
        <ReactMarkdownList
          message={jumpNode.jumpTo}
          icon={NODE_TYPES_PROPERTIES[NODE_TYPES.JUMP]?.icon}
        />
      );
    }

    const restMessageNodes = response.content.nodes.filter(node => node.type === NODE_TYPES.MESSAGE);

    // Check to see if we have more than one `restMessageNodes`
    if ((restMessageNodes.length && restMessageNodes.length - 1) || nestedList.length) {
      tags.push(
        <Popover
          content={
            <div>
              {
                restMessageNodes.map((messageNode, index) => index !== 0 && (
                  <ReactMarkdownList
                    message={messageNode?.label[currentBotLanguage]}
                    icon={NODE_TYPES_PROPERTIES[messageNode.type]?.icon}
                  />
                ))
              }
              {nestedList.map(i => i)}
            </div>
          }
          trigger="hover"
        >
          <Tag
            role="button"
            key="message"
          >
            +{restMessageNodes.length + nestedList.length + - 1}
          </Tag>
        </Popover>
      );
    }

    return tags;
  };

  const renderLivechatMessages = () => {
    if (livechatSystemMessages?.items?.length) {

      const data = filteredData.map((systemMessageActivity) => {
        const introNodeId = systemMessageActivity.content.nodes.find(node => node.type === NODE_TYPES.INTRO)?.id;
        const messageNode = systemMessageActivity.content.nodes.find(node => node.type === NODE_TYPES.MESSAGE);
        return {
          key: introNodeId,
          name: LIVECHAT_NODE_FRIENDLY_NAME[introNodeId],
          description: MESSAGE_TYPE[introNodeId],
          messageNode,
          systemMessageActivity
        };
      });

      const columns = [
        {
          title: t('Case'),
          dataIndex: 'type',
          key: 'type',
          width: '30%',
          render: (text, record) => <Typography.Text strong>{record.name}</Typography.Text>
        },
        {
          title: t('Messages & Actions'),
          dataIndex: 'message',
          key: 'message',
          render: (text, record) => (
            <Space direction="horizontal">
              {NODE_TYPES_PROPERTIES[record.messageNode?.type]?.icon}
              <Typography.Text>
                {
                  record.messageNode?.label ? (
                    <ReactMarkdown
                      className="w-100 d-flex markdown-list-inline"
                      skipHtml
                      linkTarget="_blank"
                    >
                      {record.messageNode?.label[currentBotLanguage]}
                    </ReactMarkdown>
                  ) : (
                    <Typography.Text type="secondary" className="py-4">
                      {t('(no response)')}
                    </Typography.Text>
                  )
                }
              </Typography.Text>

              {renderExtraResponseTags(record.systemMessageActivity)}
            </Space>
          )
        }
      ];

      return (
        <Form
          form={form}
          onFinish={onSubmit}
          layout="vertical"
          initialValues={initialData}
          onValuesChange={(_, allFields) => {
            updateState(allFields);
            onFieldChange();
          }}
        >
          <Table
            columns={columns}
            pagination={{
              hideOnSinglePage: true
            }}
            expandable={{
              expandedRowKeys: expandedRows,
              onExpand: (expanded, record) => {
                if (expanded) {
                  setExpandedRows( state => ([
                    ...state,
                    record.key
                  ]));
                } else {
                  setExpandedRows(
                    expandedRows.filter(row => {
                      return row !== record.key;
                    })
                  );
                }
              },
              expandedRowRender: (record) => (
                <Fragment>
                  <Typography.Paragraph type="secondary">
                    <Space wrap={false} align="start">
                      {record.description}
                    </Space>
                  </Typography.Paragraph>
                  <SystemMessageFormItems
                    form={form}
                    key={record.key}
                    systemMessageActivity={record.systemMessageActivity}
                    tenantId={tenantId}
                  />
                </Fragment>
              ),
              expandRowByClick: true
            }}
            dataSource={data}
          />
        </Form>
      );
    }

    if (isFetchingLivechatSystemMessages) {
      return (
        <Card>
          <Row justify="center">
            <Col className="p-5">
              <Spin />
            </Col>
          </Row>
        </Card>
      );
    }

    return (
      <Card>
        <Empty description={t('There are no LiveChat system messages at this moment.')} />
      </Card>
    );
  };

  return (
    renderLivechatMessages()
  );
};

SystemMessageFormItems.propTypes = {
  form: PropTypes.object.isRequired,
  systemMessageActivity: PropTypes.object.isRequired,
  tenantId: PropTypes.string.isRequired
};

LivechatSystemMessagesEditor.propTypes = {
  form: PropTypes.object.isRequired,
  tenantId: PropTypes.string.isRequired,
  onFieldChange: PropTypes.func,
  onLoadingChange: PropTypes.func
};

export default LivechatSystemMessagesEditor;
